import type { Cache, ResolveInfo } from '@urql/exchange-graphcache';
import { graphql, type ResultOf } from '@/gql/graphql';
import type { WatchEventDetailRsvpStream } from '@/screens/EventDetailHome';

const PersonEventRsvpStreamFragment = graphql(/* GraphQL */ `
  fragment PersonEventRsvpStream on person {
    id
    rsvp(args: { event_id: $eventId })
    event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
      event_id
      person_id
      notes
      status
      updated_at
    }
  }
`);

type StreamedRsvp = ResultOf<
  typeof WatchEventDetailRsvpStream
>['rsvp_stream'][number];

export type RsvpStreamCache = Pick<
  Cache,
  'inspectFields' | 'resolve' | 'invalidate'
> & {
  writeFragment: (
    fragment: typeof PersonEventRsvpStreamFragment,
    data: {
      id: string;
      rsvp: StreamedRsvp['status'];
      event_rsvp: Array<StreamedRsvp & { __typename: 'rsvp' }>;
    },
    variables: { eventId: string },
  ) => unknown;
};

export default function rsvp_stream(
  ...variables: [
    parent: {
      rsvp_stream: ResultOf<typeof WatchEventDetailRsvpStream>['rsvp_stream'];
    },
    args: unknown,
    cache: RsvpStreamCache,
    info?: ResolveInfo,
  ]
) {
  const [parent, , cache] = variables;

  const eventIds = new Set(parent.rsvp_stream.map((rsvp) => rsvp.event_id));
  const summaryKeys = new Set<string>();
  for (const eventId of eventIds) {
    const eventKey = `event:${eventId}`;
    for (const field of cache.inspectFields(eventKey)) {
      if (field.fieldName !== 'event_teams') continue;
      const teams = cache.resolve(eventKey, field.fieldKey);
      if (!Array.isArray(teams)) continue;
      for (const teamKey of teams) {
        if (typeof teamKey !== 'string') continue;
        const summaryKey = cache.resolve(teamKey, 'rsvp_summary');
        if (typeof summaryKey === 'string') summaryKeys.add(summaryKey);
      }
    }
  }
  for (const summaryKey of summaryKeys) {
    cache.invalidate(summaryKey);
  }

  for (const rsvp of parent.rsvp_stream) {
    cache.writeFragment(
      PersonEventRsvpStreamFragment,
      {
        id: rsvp.person_id,
        rsvp: rsvp.status,
        event_rsvp: [
          {
            __typename: 'rsvp',
            ...rsvp,
          },
        ],
      },
      { eventId: rsvp.event_id },
    );
  }
}
